class VariantSelects extends HTMLElement {
    constructor() {
        super();
        this.item = $(this).closest('.productView');
        this.isFullWidth = this.item.closest('.product-full-width').length == 1 || this.item.closest('.product-full-width-2').length == 1;

        this.onVariantInit();
        this.addEventListener('change', this.onVariantChange.bind(this));
    }

    onVariantInit(){
        this.updateOptions();
        this.updateMasterId();
        this.updateMedia(1500, 'init');
        // this.updateURL();
        this.renderProductAjaxInfo();
        this.renderProductInfo();
        this.refreshAvailabilityUI();
        this.updateVariantStatuses();
    }

    onVariantChange(event) {
        this.updateOptions();
        this.updateMasterId();
        this.updatePickupAvailability();
        this.updateVariantStatuses();
        this.refreshAvailabilityUI();

        if (this.currentVariant) {
            this.updateMedia(200, 'change');
            if (!document.querySelector('.featured-product')) {
                this.updateURL();
            }
            this.updateVariantInput();
            this.renderProductAjaxInfo();
            this.renderProductInfo();
            this.updateProductInfo();
            this.checkQuantityWhenVariantChange();
        }
    }

    // Applies the add-to-cart / sold-out UI for the current resolution state.
    // A combination missing from the embedded variant JSON is "unresolved", not "unavailable" —
    // checkVariantDataCompleteness() double-checks it against Shopify's full product data before
    // ever disabling purchase for a real reason.
    refreshAvailabilityUI() {
        if (this.currentVariant) {
            this.updateAttribute(false, !this.currentVariant.available);
            this.updateStickyAddToCart(false, !this.currentVariant.available);
            return;
        }

        if (this.variantLookupPending) {
            this.setPendingAvailabilityUI();
            return;
        }

        this.updateAttribute(true);
        this.updateStickyAddToCart(true);
    }

    // Keeps purchase disabled while we verify a combination that isn't in the local
    // (possibly truncated) variant data, without labelling it "Unavailable" pre-emptively.
    setPendingAvailabilityUI() {
        const addButton = document.getElementById(`product-form-${this.dataset.product}`)?.querySelector('[name="add"]');
        if (addButton) addButton.setAttribute('disabled', true);

        const stickyButton = document.getElementById(`product-form-sticky-${this.dataset.product}`)?.querySelector('[name="add"]');
        if (stickyButton) stickyButton.setAttribute('disabled', true);
    }

    // Fetches the complete, authoritative variant list for this product. Verified live: the
    // `.js` endpoint, product.variants in Liquid, and value.variant all share the exact same
    // hard cap on how many variants a product can expose to the storefront in one response —
    // a product past that cap is missing the same colours from all three, so none of them can
    // ever return more than that capped subset no matter how they're fetched. The Storefront
    // GraphQL API is the one path that supports real cursor pagination past that cap, provided a
    // Storefront API access token is configured in theme settings; without one (or if it fails),
    // this falls back to the capped `.js` fetch so products under the cap keep working unchanged.
    fetchFullVariantData() {
        if (this.fullVariantDataPromise) return this.fullVariantDataPromise;

        const fetchViaRest = () => fetch(`${this.dataset.url}.js`, { headers: { 'Accept': 'application/json' } })
            .then((response) => {
                if (!response.ok) throw new Error(`Unable to resolve variant data (${response.status})`);
                return response.json();
            })
            .then((product) => product.variants);

        this.fullVariantDataPromise = (
            window.storefrontApi && window.storefrontApi.token
                // Only the Storefront API path is actually authoritative/complete (real
                // pagination past the cap) — mark it as such so a combination that still
                // doesn't match after this is trusted as genuinely nonexistent, instead of
                // falling through to resolveVariantOutOfBand()'s unreliable server-side
                // value.variant guess (see checkVariantDataCompleteness).
                ? this.fetchAllVariantsViaStorefrontApi().then((variants) => {
                    this.variantDataComplete = true;
                    return variants;
                }).catch((error) => {
                    console.error('Storefront API variant fetch failed, falling back to capped data:', error);
                    return fetchViaRest();
                })
                : fetchViaRest()
        ).catch((error) => {
            console.error('Variant resolution failed:', error);
            this.fullVariantDataPromise = null;
            return null;
        });

        return this.fullVariantDataPromise;
    }

    // Pages through the Storefront GraphQL API's variants connection (250 per page, like the
    // capped sources, but with real hasNextPage/endCursor pagination) until every variant on the
    // product has been collected, then normalizes each into the same shape as the REST/Liquid
    // variant objects used everywhere else in this file (option1/2/3, available, id, etc.) so no
    // other code here needs to know which source the data came from.
    async fetchAllVariantsViaStorefrontApi() {
        try {
            return await this.fetchAllVariantsViaStorefrontApiQuery(true);
        } catch (error) {
            // quantityAvailable needs the separate unauthenticated_read_product_inventory
            // scope, which may not be granted on the configured token — retry without that
            // one field rather than losing the complete variant list entirely over it.
            console.warn('Storefront API fetch with quantityAvailable failed, retrying without it:', error);
            return this.fetchAllVariantsViaStorefrontApiQuery(false);
        }
    }

    async fetchAllVariantsViaStorefrontApiQuery(includeInventoryQuantity) {
        const handle = this.dataset.url.split('/').filter(Boolean).pop();
        const endpoint = `https://${window.storefrontApi.domain}/api/${window.storefrontApi.apiVersion}/graphql.json`;
        const query = `
            query ProductVariants($handle: String!, $cursor: String) {
                productByHandle(handle: $handle) {
                    variants(first: 250, after: $cursor) {
                        pageInfo { hasNextPage endCursor }
                        edges {
                            node {
                                id
                                title
                                sku
                                barcode
                                availableForSale
                                ${includeInventoryQuantity ? 'quantityAvailable' : ''}
                                selectedOptions { value }
                                price { amount }
                                compareAtPrice { amount }
                                image { id url altText width height }
                            }
                        }
                    }
                }
            }
        `;

        const variants = [];
        let cursor = null;
        let hasNextPage = true;

        while (hasNextPage) {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': window.storefrontApi.token,
                },
                body: JSON.stringify({ query, variables: { handle, cursor } }),
            });
            if (!response.ok) throw new Error(`Storefront API request failed (${response.status})`);

            const json = await response.json();
            if (json.errors) throw new Error(json.errors.map((e) => e.message).join('; '));

            const connection = json.data?.productByHandle?.variants;
            if (!connection) throw new Error('Unexpected Storefront API response shape');

            connection.edges.forEach(({ node }) => variants.push(this.normalizeStorefrontVariant(node)));
            hasNextPage = connection.pageInfo.hasNextPage;
            cursor = connection.pageInfo.endCursor;
        }

        return variants;
    }

    // Shapes a Storefront API variant node into the same fields the REST/Liquid-sourced variant
    // objects carry, so findVariantMatch/updateVariantStatuses/etc. work unchanged either way.
    normalizeStorefrontVariant(node) {
        const options = node.selectedOptions.map((option) => option.value);
        return {
            id: parseInt(node.id.split('/').pop(), 10),
            title: node.title,
            sku: node.sku,
            barcode: node.barcode,
            available: node.availableForSale,
            // null when quantityAvailable wasn't requested/granted — callers must treat null
            // as "no reliable number", not zero.
            inventory_quantity: node.quantityAvailable ?? null,
            inventory_management: 'shopify',
            options,
            option1: options[0] ?? null,
            option2: options[1] ?? null,
            option3: options[2] ?? null,
            price: Math.round(parseFloat(node.price.amount) * 100),
            compare_at_price: node.compareAtPrice ? Math.round(parseFloat(node.compareAtPrice.amount) * 100) : null,
            featured_media: node.image ? {
                alt: node.image.altText,
                id: node.image.id ? parseInt(node.image.id.split('/').pop(), 10) : node.image.url,
                preview_image: {
                    src: node.image.url,
                    width: node.image.width,
                    height: node.image.height,
                    aspect_ratio: node.image.width && node.image.height ? node.image.width / node.image.height : null,
                },
            } : null,
        };
    }

    findVariantMatch(variants) {
        if (!variants) return undefined;
        return variants.find((variant) => {
            return !variant.options.map((option, index) => this.options[index] === option).includes(false);
        });
    }

    // The Liquid picker leaves data-variant-id="" on an input/label when no variant matched
    // that value server-side (same gap as above). Backfill it once fuller data is in, since
    // other scripts (e.g. the "available sizes" swatch widget) read that attribute directly.
    syncVariantIdAttributes() {
        const inputWrappers = [...this.querySelectorAll('.product-form__input')];
        inputWrappers.forEach((wrapper, index) => {
            [...wrapper.querySelectorAll('input[type="radio"], option')].forEach((el) => {
                if (el.getAttribute('data-variant-id')) return;
                const value = el.getAttribute('value');
                const match = this.variantData.find((variant) => variant[`option${index + 1}`] === value);
                if (!match) return;
                el.setAttribute('data-variant-id', match.id);
                if (el.nextElementSibling) el.nextElementSibling.setAttribute('data-variant-id', match.id);
            });
        });
    }

    // A rendered option value with no entry in this.variantData at its option slot means
    // the local (embedded) data doesn't cover every value the picker displays — e.g. a
    // high-variant product where the page's embedded JSON is a subset of the real variants.
    // Checked purely from already-rendered DOM + local data, so normal products (where every
    // rendered value already has a matching entry) never pay for the extra network request.
    hasUnresolvedOptionValues() {
        const inputWrappers = [...this.querySelectorAll('.product-form__input')];
        return inputWrappers.some((wrapper, index) => {
            const knownValues = new Set(this.variantData.map((variant) => variant[`option${index + 1}`]));
            return [...wrapper.querySelectorAll('input[type="radio"], option')].some((el) => {
                const value = el.getAttribute('value');
                return value != null && value !== '' && !knownValues.has(value);
            });
        });
    }

    // A later option (e.g. Size) whose PREVIOUSLY selected option's value (e.g. the picked
    // Colour) has zero matching entries in the local variant data at all. Distinct from
    // hasUnresolvedOptionValues(): that catches a value missing everywhere; this catches an
    // entire selected combination missing from the local data, which is exactly when
    // updateVariantStatuses()'s local-only check has nothing reliable to go on and must not be
    // trusted to mark anything sold out — only a real, correctly scoped section render
    // (resolveVariantOutOfBand) can tell us the truth for this colour's sizes.
    hasScopeGap() {
        const inputWrappers = [...this.querySelectorAll('.product-form__input')];
        for (let index = 1; index < inputWrappers.length; index++) {
            const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked')?.value;
            const hasLocalData = this.variantData.some((variant) => variant[`option${index}`] === previousOptionSelected);
            if (!hasLocalData) return true;
        }
        return false;
    }

    // Fetches a section render for a KNOWN variant id and returns the raw HTML. NOT a reliable
    // way to resolve a value's TRUE availability for the current selection, despite appearances:
    // verified live that the returned fieldsets still fall back to Shopify's own value.variant
    // per-value lookup for anything outside product.variants' cap, and that lookup isn't scoped
    // to the current selection either — it happily reports a nonexistent Colour+Size combination
    // as "available" by pointing at a completely different colour's real variant. Only use this
    // (via resolveVariantOutOfBand) as an absolute last resort when there's no complete variant
    // data to check against at all (see checkVariantDataCompleteness / variantDataComplete).
    fetchSectionHtml(variantId) {
        return fetch(`${this.dataset.url}?variant=${variantId}&section_id=${this.dataset.section}`)
            .then((response) => (response.ok ? response.text() : null))
            .catch(() => null);
    }

    // Resolves the exact variant for the current selection by walking the option fieldsets
    // left to right: start from whichever option already has a real data-variant-id (Liquid's
    // value.variant, itself immune to the cap), fetch that variant's section render, read the
    // NEXT option's currently-selected value's id out of the now-correctly-scoped response, and
    // repeat. Each step narrows the combination by one more option instead of scanning a capped
    // list for the whole thing at once.
    async resolveVariantOutOfBand() {
        const fieldsets = [...this.querySelectorAll('.product-form__input')];
        if (!fieldsets.length) return null;

        const findChecked = (fieldset) => fieldset.querySelector('input[type="radio"]:checked')
            || fieldset.querySelector('select')?.selectedOptions?.[0];

        let anchorInput = findChecked(fieldsets[0]);
        let anchorId = anchorInput?.getAttribute('data-variant-id');
        if (!anchorId) return null;

        let anchorLabel = anchorInput.tagName === 'SELECT' ? null : anchorInput.nextElementSibling;

        for (let i = 1; i < fieldsets.length; i++) {
            const html = await this.fetchSectionHtml(anchorId);
            if (!html) return null;

            const doc = new DOMParser().parseFromString(html, 'text/html');
            const remoteFieldset = [...doc.querySelectorAll('.product-form__input')][i];
            if (!remoteFieldset) return null;

            // This option's local buttons were left showing whichever colour rendered
            // server-side on initial load (or whatever was last resolved) — restyle every
            // value here from the render that's actually scoped to the current selection,
            // not just the one value we're resolving an id for.
            this.applyRemoteFieldsetState(fieldsets[i], remoteFieldset);

            const wantedValue = this.options[i];
            const remoteInput = [...remoteFieldset.querySelectorAll('input[type="radio"], option')]
                .find((el) => el.getAttribute('value') === wantedValue);
            const remoteId = remoteInput?.getAttribute('data-variant-id');
            if (!remoteId) return null;

            anchorId = remoteId;
            anchorLabel = remoteInput.tagName === 'OPTION' ? null : remoteInput.nextElementSibling;
        }

        const available = anchorLabel ? !anchorLabel.classList.contains('soldout') : true;
        return { id: anchorId, available };
    }

    // Copies each value's resolved data-variant-id and available/soldout/unavailable class
    // from a section render that's correctly scoped to the current selection onto the live
    // fieldset, so a dependent option (e.g. Size) visually re-syncs to the colour actually
    // picked instead of staying stuck showing whichever colour rendered on initial page load.
    applyRemoteFieldsetState(localFieldset, remoteFieldset) {
        const remoteInputs = [...remoteFieldset.querySelectorAll('input[type="radio"], option')];
        [...localFieldset.querySelectorAll('input[type="radio"], option')].forEach((localEl) => {
            const remoteEl = remoteInputs.find((el) => el.getAttribute('value') === localEl.getAttribute('value'));
            if (!remoteEl) return;

            const remoteId = remoteEl.getAttribute('data-variant-id');
            if (remoteId) localEl.setAttribute('data-variant-id', remoteId);

            const localLabel = localEl.tagName === 'OPTION' ? null : localEl.nextElementSibling;
            const remoteLabel = remoteEl.tagName === 'OPTION' ? null : remoteEl.nextElementSibling;
            if (localLabel && remoteLabel) {
                localLabel.className = remoteLabel.className;
                if (remoteId) localLabel.setAttribute('data-variant-id', remoteId);
            }
        });
    }

    // Neither a missing current-variant match nor an unresolved option value is assumed sold
    // out. The one-time data sync (fetch + merge full variant data) only ever needs to run
    // once per page load — variantDataSynced latches that. But a missing currentVariant match
    // must be retried on EVERY selection that's missing one, not just the first — otherwise
    // clicking a second capped-out value after the first sync already ran would short-circuit
    // straight past resolveVariantOutOfBand() and get stuck "unavailable".
    checkVariantDataCompleteness() {
        if (this.variantLookupPending) return;

        const missingCombination = !this.currentVariant;
        const needsSync = !this.variantDataSynced && this.hasUnresolvedOptionValues();
        // A scope gap (e.g. the selected Colour has no local data at all) doesn't necessarily
        // mean currentVariant is missing — the currently CHECKED size might happen to also be
        // valid for this colour, resolving fine, while the OTHER (unselected) size buttons in
        // that same fieldset are still stuck with stale/wrong classes. Must still trigger the
        // out-of-band walk purely to refresh those, not just when the selection itself fails.
        const scopeGap = this.hasScopeGap();
        if (!missingCombination && !needsSync && !scopeGap) return;

        this.variantLookupPending = true;
        if (missingCombination) this.setPendingAvailabilityUI();

        const requestedOptions = this.options;

        const syncStep = needsSync
            ? this.fetchFullVariantData().then((variants) => {
                if (!variants) return;
                // Merge the full list in so later lookups (statuses, swatches) see every variant too.
                this.variantData = variants;
                this.syncVariantIdAttributes();
                this.variantDataSynced = true;
                if (requestedOptions !== this.options) return; // selection changed while we were fetching
                if (!this.currentVariant) this.currentVariant = this.findVariantMatch(variants);
            })
            : Promise.resolve();

        syncStep
            .then(() => {
                if (requestedOptions !== this.options) return;
                if (this.currentVariant && !this.hasScopeGap()) return;
                // resolveVariantOutOfBand() ultimately still trusts the server's own
                // value.variant pick for each fieldset it walks — the exact same per-value
                // lookup that isn't scoped to the other selected options and can hand back a
                // completely different colour's variant (verified live: it happily reported a
                // nonexistent Colour+Size combination as "available", pointing at another
                // colour's real variant). That's an acceptable last resort when this.variantData
                // is genuinely incomplete (no Storefront API token configured, or the fetch
                // failed) — but once fetchFullVariantData() has actually returned the complete,
                // authoritative list (variantDataComplete), a combination that still doesn't
                // match in THAT data is a real "doesn't exist", full stop — must not be
                // second-guessed by the unreliable fallback, or a nonexistent combination can
                // come back "available" pointing at the wrong product entirely.
                if (this.variantDataComplete) return;
                return this.resolveVariantOutOfBand().then((resolved) => {
                    if (resolved && requestedOptions === this.options && !this.currentVariant) this.currentVariant = resolved;
                });
            })
            .finally(() => {
                this.variantLookupPending = false;
                if (requestedOptions !== this.options) return; // a newer change already re-ran this flow
                this.updateVariantStatuses();
                this.refreshAvailabilityUI();
                if (this.currentVariant) {
                    this.updateMedia(200, 'change');
                    if (!document.querySelector('.featured-product')) {
                        this.updateURL();
                    }
                    this.updateVariantInput();
                    this.renderProductAjaxInfo();
                    this.renderProductInfo();
                    this.updateProductInfo();
                    this.checkQuantityWhenVariantChange();
                }
            });
    }

    updateOptions() {
        this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
    }

    decodeOptions() {
        this.options = this.options.map(option => {
            const parsedOption = this.decodeOption(option)
            return parsedOption
        })
    }
  
    decodeOption(option) {
      if (option) {
          return option.split('Special_Double_Quote').join('"').split('Special_Slash').join('/')
        } else {
          return null
        }
    }

    encodeOption(option) {
        if (option) {
          return option.split('"').join('Special_Double_Quote').split('/').join('Special_Slash')
        } else {
          return null
        }
    }
    
    updateMasterId() {
        this.decodeOptions()
        this.currentVariant = this.findVariantMatch(this.getVariantData());
        this.checkVariantDataCompleteness();

        if (this.item.find('[data-filter]').length && this.currentVariant?.featured_media && this.currentVariant.featured_media.alt != null) {
            this.item.find('[data-filter]:checked').attr('data-value-default-lang', this.currentVariant?.featured_media.alt.toLowerCase().replace(/ /g,"-"));
        }
    }

    updateMedia(time, status) {
        const enableVariantImageGroup = document?.querySelector('.enable_variant_image_group');
    
        if (enableVariantImageGroup && status == 'init') return;

        setTimeout(() => {
            if (!this.currentVariant || !this.currentVariant?.featured_media || document.querySelector('.productView-nav')?.matches('.media-filter')) return;
            
            const newMedia = document.querySelectorAll(
                `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
            );
    
            if (!newMedia) return;
            window.setTimeout(() => {
                $(newMedia).trigger('click');
            }, time);
    
            if (!this.isFullWidth || window.innerWidth < 768 || !this.currentVariant) return;
            const mediaId = this.currentVariant.featured_media.id;
            const activeMedia = document?.querySelector(`.product-single__media[data-media-id="${mediaId}"]`);
            const fallbackImageContainer = document?.querySelector('.productView-image[data-index="1"]');

            if (activeMedia) {
                setTimeout(() => {
                    activeMedia.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 20);
            } else {
                const fallbackImage = fallbackImageContainer?.querySelector('img');
                const featuredImage = this.currentVariant?.featured_image;
                if (!featuredImage || !fallbackImage) return;

                fallbackImage.setAttribute('src', featuredImage.src);
                fallbackImage.setAttribute('srcset', featuredImage.src);
                fallbackImage.setAttribute('alt', featuredImage.alt);

                setTimeout(() => {
                    fallbackImageContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 20);
            }
        }, 1)
    }

    scrollToBlock(block) {
        const headerHeight = document.querySelectorAll('.section-header-navigation')[0]?.getBoundingClientRect().height 
        const announcementBarHeight = document.querySelectorAll('.announcement-bar')[0]?.getBoundingClientRect().height 
        const positionTop = block.getBoundingClientRect().top - headerHeight - announcementBarHeight 

        window.scrollTo({
            top: positionTop,
            behavior: 'smooth'
        })
    }

    updateURL() {
        if (!this.currentVariant) return;
        window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput() {
        const productForms = document.querySelectorAll(`#product-form-${this.dataset.product}, #product-form-installment-${this.dataset.product}`);

        productForms.forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = this.currentVariant.id;
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    updatePickupAvailability() {
        const pickUpAvailability = document.querySelector('pickup-availability');
        if (!pickUpAvailability) return;

        if (this.currentVariant?.available) {
            pickUpAvailability.fetchAvailability(this.currentVariant.id);
        } else {
            pickUpAvailability.removeAttribute('available');
            pickUpAvailability.innerHTML = '';
        }
    }

    renderProductAjaxInfo() {
        fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
            .then((response) => response.text())
            .then((responseText) => {
                const id = `product-price-${this.dataset.product}`;
                const html = new DOMParser().parseFromString(responseText, 'text/html')
                const destination = document.getElementById(id);
                const source = html.getElementById(id);

                const property = `product-property-${this.dataset.product}`;
                const destinationProperty = document.getElementById(property);
                const sourceProperty = html.getElementById(property);

                if (source && destination) {
                    destination.innerHTML = source.innerHTML;
                }

                if (this.checkNeedToConvertCurrency()) {
                    let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');

                    Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
                }

                if(destinationProperty) {
                    if (sourceProperty) {
                        destinationProperty.innerHTML = sourceProperty.innerHTML;
                        destinationProperty.style.display = 'table';
                    } else{
                        destinationProperty.style.display = 'none';
                    }
                } else if (sourceProperty) {
                    document.querySelector('.productView-product').insertBefore(sourceProperty, document.querySelector('.productView-options'));
                }

                document.getElementById(`product-price-${this.dataset.product}`)?.classList.remove('visibility-hidden');
                this.updateBadgeSale()
        });
    }

    // Applies a KNOWN, reliable quantity number (from either the capped static array or the
    // complete Storefront API data) to the quantity cap and Availability display — the one place
    // both sources converge, so the actual display/cap logic only needs to exist once.
    applyInventoryQuantity(inventoryQuantity, inputQuantity, buttonSubmit) {
        if(inputQuantity.length > 0) {
            inputQuantity.attr('data-inventory-quantity', inventoryQuantity);
        } else {
            buttonSubmit.attr('data-inventory-quantity', inventoryQuantity);
        }

        if(this.item.find('[data-inventory]').length > 0){
            if(inventoryQuantity > 0){
                const showStock = this.item.find('[data-inventory]').data('stock-level');
                if (showStock == 'show') {
                    this.item.find('[data-inventory] .productView-info-value').text(inventoryQuantity+' '+window.inventory_text.inStock);
                }
                else {
                    this.item.find('[data-inventory] .productView-info-value').text(window.inventory_text.inStock);
                }
            } else {
                this.item.find('[data-inventory] .productView-info-value').text(window.inventory_text.outOfStock);
            }
        }

        hotStock(inventoryQuantity);
    }

    renderProductInfo() {
        if(this.item.find('[data-sku]').length > 0){
            this.item.find('[data-sku] .productView-info-value').text(this.currentVariant.sku);
        }

        if(this.item.find('[data-barcode]').length > 0){
            this.item.find('[data-barcode] .productView-info-value').text(this.currentVariant.barcode);
        }

        var inventory = this.currentVariant?.inventory_management;

        if(inventory != null) {
            var arrayInVarName = `product_inven_array_${this.dataset.product}`,
                inven_array = window[arrayInVarName],
                inputQuantity = this.item.find('input[name="quantity"]'),
                buttonSubmit = this.item.find('button#product-add-to-cart');

            if(inven_array != undefined) {
                var inven_num = inven_array[this.currentVariant.id];

                if (inven_num !== undefined) {
                    this.applyInventoryQuantity(parseInt(inven_num), inputQuantity, buttonSubmit);
                } else if (this.currentVariant.inventory_quantity !== null && this.currentVariant.inventory_quantity !== undefined) {
                    // product_inven_array is built server-side from product.variants, which is
                    // capped the same way the swatch/size picker data used to be — this variant's
                    // id simply isn't a key in it. But the complete Storefront API data carries a
                    // real quantityAvailable for it (once the unauthenticated_read_product_inventory
                    // scope is granted), so use that instead of falling back to a guess.
                    this.applyInventoryQuantity(this.currentVariant.inventory_quantity, inputQuantity, buttonSubmit);
                } else {
                    // No reliable quantity number exists anywhere for this variant (missing from
                    // the capped array, and no Storefront API inventory scope granted) — trust the
                    // accurate `available` flag instead of guessing "0 stock", and skip the
                    // exact-count display / hotStock() messaging rather than showing wrong numbers.
                    inputQuantity.removeAttr('data-inventory-quantity');
                    buttonSubmit.removeAttr('data-inventory-quantity');

                    if(this.item.find('[data-inventory]').length > 0){
                        this.item.find('[data-inventory] .productView-info-value').text(
                            this.currentVariant.available ? window.inventory_text.inStock : window.inventory_text.outOfStock
                        );
                    }
                }
            }
        }

        if(this.item.find('.productView-stickyCart').length > 0){
            const sticky = this.item.find('.productView-stickyCart');
            const optionSticky = sticky.find('.select__select');

            optionSticky.val(this.currentVariant.id);
        }
    }

    updateProductInfo() {
        fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
            .then((response) => response.text())
            .then((responseText) => {
                const description = `[data-product-description-${this.dataset.product}]`
                const html = new DOMParser().parseFromString(responseText, 'text/html')
                const destinationDesc = document.querySelector(description);
                const sourceDesc = html.querySelector(description);

                if (sourceDesc && destinationDesc) {
                    destinationDesc.innerHTML = sourceDesc.innerHTML;
                    if (destinationDesc.closest('.toggle-content--height')){
                        destinationDesc.style.maxHeight = null;
                    }
                }
        });
    }

    updateAttribute(unavailable = true, disable = true){
        const addButton = document.getElementById(`product-form-${this.dataset.product}`)?.querySelector('[name="add"]');
        var quantityInput = this.item.find('input[name="quantity"]'),
            notifyMe = this.item.find('.productView-notifyMe'),
            hotStock = this.item.find('.productView-hotStock'),
            buttonAddtocart = this.item.find('.product-form__submit'),
            maxValue = parseInt(quantityInput.attr('data-inventory-quantity'));

        if (isNaN(maxValue)) {
            maxValue = maxValue = parseInt(buttonAddtocart.attr('data-inventory-quantity'));
        } else {
            maxValue = parseInt(quantityInput.attr('data-inventory-quantity'));
        }
        
        if(unavailable){
            var text = window.variantStrings.unavailable;

            quantityInput.attr('disabled', true);
            notifyMe.slideUp('slow');
            if (addButton != null){
                addButton.setAttribute('disabled', true);
                addButton.textContent = text;
            }
            quantityInput.closest('quantity-input').addClass('disabled');

            if(hotStock.length > 0) hotStock.addClass('is-hiden');
        } else {
            if (disable) {
                var text = window.variantStrings.soldOut,
                    subTotal = 0,
                    price = this.currentVariant?.price;

                const stickyPrice = $('[data-sticky-add-to-cart] .money-subtotal .money');
                const stickyComparePrice = $('[data-sticky-add-to-cart] .money-compare-price .money');

                if (window.subtotal.show) {
                    let qty = quantityInput.val() || 1;

                    subTotal = qty * price;
                    subTotal = Shopify.formatMoney(subTotal, window.money_format);
                    subTotal = extractContent(subTotal);

                    const moneySpan = document.createElement('span')
                    moneySpan.classList.add(window.currencyFormatted ? 'money' : 'money-subtotal')
                    moneySpan.innerText = subTotal
                    document.body.appendChild(moneySpan)

                    if (this.checkNeedToConvertCurrency()) {
                        let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');
                        Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
                    }

                    subTotal = moneySpan.innerText
                    $(moneySpan).remove()

                    if (window.subtotal.style == '1') {
                        const pdView_subTotal = document.querySelector('.productView-subtotal .money') || document.querySelector('.productView-subtotal .money-subtotal');
                        if (pdView_subTotal != null) {
                            pdView_subTotal.textContent = subTotal;
                        }
                    } else if (window.subtotal.style == '2') {
                        text = window.subtotal.text.replace('[value]', subTotal);
                    }
                } else {
                    subTotal = Shopify.formatMoney(price, window.money_format);
                    subTotal = extractContent(subTotal);
                }

                quantityInput.attr('data-price', this.currentVariant?.price);
                quantityInput.attr('disabled', true);
                if (addButton != null){
                    addButton.setAttribute('disabled', true);
                    addButton.textContent = text;
                }
                quantityInput.closest('quantity-input').addClass('disabled');

                if (subTotal != 0 && stickyPrice.length) {
                    stickyPrice.text(subTotal);
                }

                const thisStickyPrice = $('[data-sticky-add-to-cart] .sticky-price');
                const thisComparePrice = $('[data-sticky-add-to-cart] .money-compare-price');
                const compare_at_price = this.currentVariant?.compare_at_price;
                const current_at_price = this.currentVariant?.price;

                if(compare_at_price == current_at_price) {
                    thisStickyPrice.removeClass('has-compare-price');
                    thisComparePrice.remove();
                } else {
                    if (compare_at_price) {
                        thisStickyPrice.addClass('has-compare-price');
                        if (thisComparePrice.length) {
                            thisComparePrice.attr('data-compare-price', compare_at_price);
                        } else {
                            thisStickyPrice.prepend(`<s class="money-compare-price" data-compare-price="${compare_at_price}"><span class="money"></span></s>`);
                        }
                    } else {
                        thisStickyPrice.removeClass('has-compare-price');
                        thisComparePrice.remove();
                    }
                }

                if (subTotal != 0 && stickyComparePrice.length && window.subtotal.show) {
                    let comparePrice = $('[data-sticky-add-to-cart] .money-compare-price').data('compare-price'),
                        qty = quantityInput.val() || 1;
                    comparePrice = qty * comparePrice;
                    comparePrice = Shopify.formatMoney(comparePrice, window.money_format);
                    comparePrice = extractContent(comparePrice);
                    stickyComparePrice.text(comparePrice);
                }

                if (notifyMe.length > 0) {
                    notifyMe.find('.halo-notify-product-variant').val(this.currentVariant.title);
                    notifyMe.find('.notifyMe-text').empty();
                    notifyMe.slideDown('slow');
                }
            } else {
                var text,
                    subTotal = 0,
                    price = this.currentVariant?.price;

                const stickyPrice = $('[data-sticky-add-to-cart] .money-subtotal .money');

                if (window.subtotal.show) {
                    let qty = quantityInput.val() || 1;

                    subTotal = qty * price;
                    subTotal = Shopify.formatMoney(subTotal, window.money_format);
                    subTotal = extractContent(subTotal);

                    const moneySpan = document.createElement('span')
                    moneySpan.classList.add(window.currencyFormatted ? 'money' : 'money-subtotal')
                    moneySpan.innerText = subTotal
                    document.body.appendChild(moneySpan)

                    if (this.checkNeedToConvertCurrency()) {
                        let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');
                        Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
                    }

                    subTotal = moneySpan.innerText
                    $(moneySpan).remove()

                    if (window.subtotal.style == '1') {
                        const pdView_subTotal = document.querySelector('.productView-subtotal .money') || document.querySelector('.productView-subtotal .money-subtotal');
                        if (pdView_subTotal != null) {
                            pdView_subTotal.textContent = subTotal;
                        }

                        if (this.currentVariant.available && maxValue <= 0 && this.currentVariant.inventory_management == "shopify") {
                            text = window.variantStrings.preOrder;
                        } else {
                            text = window.variantStrings.addToCart;
                        }
                    } else if (window.subtotal.style == '2') {
                        if (this.currentVariant.available && maxValue <= 0 && this.currentVariant.inventory_management == "shopify") {
                            text = window.variantStrings.preOrder;
                        } else {
                            text = window.subtotal.text.replace('[value]', subTotal);
                            $('#show-sticky-product').text(text);
                            $('#product-sticky-add-to-cart').text(text);
                        }
                    }
                } else {
                    subTotal = Shopify.formatMoney(price, window.money_format);
                    subTotal = extractContent(subTotal);
                    if (this.currentVariant.available && maxValue <= 0 && this.currentVariant.inventory_management == "shopify") {
                        text = window.variantStrings.preOrder;
                    } else {
                        text = window.variantStrings.addToCart;
                    }
                }

                quantityInput.attr('data-price', this.currentVariant?.price);
                quantityInput.attr('disabled', false);
                if (addButton != null){
                    addButton.removeAttribute('disabled');
                    addButton.textContent = text;
                }
                quantityInput.closest('quantity-input').removeClass('disabled');

                if (subTotal != 0 && stickyPrice.length) {
                    stickyPrice.text(subTotal);
                }

                const thisStickyPrice = $('[data-sticky-add-to-cart] .sticky-price');
                const thisComparePrice = $('[data-sticky-add-to-cart] .money-compare-price');
                const compare_at_price = this.currentVariant?.compare_at_price;
                const current_at_price = this.currentVariant?.price;

                if(compare_at_price == current_at_price) {
                    thisStickyPrice.removeClass('has-compare-price');
                    thisComparePrice.remove();
                } else {
                    if (compare_at_price) {
                        thisStickyPrice.addClass('has-compare-price');
                        if (thisComparePrice.length) {
                            thisComparePrice.attr('data-compare-price', compare_at_price);
                            
                            const compare_at_price_sticky = Shopify.formatMoney(compare_at_price, window.money_format);
                            const comparePrice_sticky = extractContent(compare_at_price_sticky);
                            thisComparePrice.text(comparePrice_sticky);
                            
                            if (this.checkNeedToConvertCurrency()) {
                                let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');
                                Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
                            }

                            thisStickyPrice.prepend(`<s class="money-compare-price" data-compare-price="${compare_at_price}"><span class="money"></span></s>`);
                            thisComparePrice.remove();
                        } else {
                            thisStickyPrice.prepend(`<s class="money-compare-price" data-compare-price="${compare_at_price}"><span class="money"></span></s>`);
                        }
                    } else {
                        thisStickyPrice.removeClass('has-compare-price');
                        thisComparePrice.remove();
                    }
                }

                const stickyComparePrice = $('[data-sticky-add-to-cart] .money-compare-price .money');
                if (subTotal != 0 && stickyComparePrice.length && window.subtotal.show) {
                    let comparePrice = $('[data-sticky-add-to-cart] .money-compare-price').data('compare-price'),
                        qty = quantityInput.val() || 1;
                    comparePrice = qty * comparePrice;
                    comparePrice = Shopify.formatMoney(comparePrice, window.money_format);
                    comparePrice = extractContent(comparePrice);
                    stickyComparePrice.text(comparePrice);
                }

                if (notifyMe.length > 0) {
                    notifyMe.slideUp('slow');
                }
            }
        }
    }

    updateStickyAddToCart(unavailable = true, disable = true){
        if(this.item.find('.productView-stickyCart').length > 0){
            const sticky = this.item.find('.productView-stickyCart');
            const itemImage = sticky.find('.sticky-image');
            const option = sticky.find('.select__select');
            const input = document.getElementById(`product-form-sticky-${this.dataset.product}`)?.querySelector('input[name="id"]');
            const button = document.getElementById(`product-form-sticky-${this.dataset.product}`)?.querySelector('[name="add"]');
            var quantityInput = this.item.find('input[name="quantity"]');
            var submitBtn = $('.product-form__submit');
            var maxValue;

            if (quantityInput.length > 0) {
                maxValue = parseInt(quantityInput.attr('data-inventory-quantity'));
            } else {
                maxValue = parseInt(submitBtn.attr('data-inventory-quantity'));
            }
          
            if(unavailable){
                var text = window.variantStrings.unavailable;

                button.setAttribute('disabled', true);
                button.textContent = text;
            } else {
                if (!this.currentVariant) return;

                const image = this.currentVariant?.featured_image;
                
                if (image != null) {
                    itemImage.find('img').attr({
                        'src': image.src,
                        'srcset': image.src,
                        'alt': image.alt
                    });
                }

                option.val(this.currentVariant.id);

                if (disable) {
                    var text = window.variantStrings.soldOut;

                    button.setAttribute('disabled', true);
                    button.textContent = text;
                } else {
                    if (this.currentVariant.available && maxValue <= 0 && this.currentVariant.inventory_management == "shopify") {
                        text = window.variantStrings.preOrder;
                    } else {
                        text = window.variantStrings.addToCart;
                    }

                    button.removeAttribute('disabled');
                    button.textContent = text;
                }

                input.value = this.currentVariant.id;
                input.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    }

    getVariantData() {
        this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
        return this.variantData;
    }

    checkNeedToConvertCurrency() {
        var currencyItem = $('.dropdown-item[data-currency]');
        if (currencyItem.length) {
            return (window.show_multiple_currencies && Currency.currentCurrency != shopCurrency) || window.show_auto_currency;
        } else {
            return;
        }
    }

    checkQuantityWhenVariantChange() {
        var quantityInput = this.closest('.productView-details').querySelector('input.quantity__input')
        var maxValue = parseInt(quantityInput?.dataset.inventoryQuantity);
        var inputValue = parseInt(quantityInput?.value);

        let value = inputValue 

        if (inputValue > maxValue && maxValue > 0) {
            value = maxValue
        } else {
            value = inputValue
        }

        if (value < 1 || isNaN(value)) value = 1 
      
        if (quantityInput) {
          quantityInput.value = value
        }
      
        // document.getElementById('product-add-to-cart').dataset.available = this.currentVariant.available && maxValue <= 0
        const elementProductAddToCart = document.getElementById('product-add-to-cart');
        if (elementProductAddToCart && this.currentVariant?.available !== undefined && typeof maxValue !== 'undefined') {
            elementProductAddToCart.dataset.available = this.currentVariant.available && maxValue <= 0;
        }
    }

    updateVariantStatuses() {
        // const options = this.item.find('.productView-details .product-form__input'),
        //     optionsLength = options.length,
        //     pvOptionsLength = PVoptions.length,
        //     checkStickyVariant = false;
      
        // optionsLength > pvOptionsLength ? checkStickyVariant = true : '';

        const selectedOptionOneVariants = this.variantData.filter(variant => this.querySelector(':checked').value === variant.option1);
        const inputWrappers = [...this.querySelectorAll('.product-form__input')];
        const inputLength = inputWrappers.length;
        const variant_swatch = [...this.querySelectorAll('.product-form__swatch')];
        inputWrappers.forEach((option, index) => {
            option.querySelector('[data-header-option]').innerText = option.querySelector(':checked').value;
            const optionInputs = [...option.querySelectorAll('input[type="radio"], option')]
            // The first option (e.g. Colour) has no preceding option to filter by — its
            // availability is computed against the full variant data, same as a single-option
            // product, instead of being skipped (that used to leave it permanently stuck with
            // whatever "soldout"/"available" class Liquid rendered at page load).
            let optionInputsValue, availableOptionInputsValue;
            // Unscoped by the previously selected option(s) — every value seen anywhere in the
            // local variant data at this option slot. Only meaningful together with
            // currentScopeHasLocalData below: for a product whose full variant list doesn't fit
            // in the page's local data (e.g. many colours), the local data can be missing the
            // CURRENTLY SELECTED colour's variants entirely — in which case "not found locally"
            // means "we simply have no data for this colour", not "sold out", and must be left
            // alone rather than marked sold out just because the value exists for some other colour.
            const knownValuesAtPosition = this.variantData.map(variantOption => variantOption[`option${ index + 1 }`]);
            let currentScopeHasLocalData = true;
            if (index > 0 && inputLength > 1) {
                const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
                const scopedVariants = selectedOptionOneVariants.filter(variant => variant[`option${ index }`] === previousOptionSelected);
                currentScopeHasLocalData = scopedVariants.length > 0;
                optionInputsValue = scopedVariants.map(variantOption => variantOption[`option${ index + 1 }`]);
                availableOptionInputsValue = scopedVariants.filter(variant => variant.available).map(variantOption => variantOption[`option${ index + 1 }`]);
            } else {
                optionInputsValue = knownValuesAtPosition;
                availableOptionInputsValue = this.variantData.filter(variant => variant.available).map(variantOption => variantOption[`option${ index + 1 }`]);
            }
            this.setInputAvailability(optionInputs, optionInputsValue, availableOptionInputsValue, knownValuesAtPosition, currentScopeHasLocalData)
            if (variant_swatch.length > 1){
                this.updateImageSwatch(selectedOptionOneVariants)
            }
        });
    }

    updateImageSwatch(selectedOptionOneVariants) {
        const inputWrappers = this.querySelectorAll('.product-form__input');
        if(inputWrappers){
            inputWrappers.forEach((element, inputIndex) => {
                const imageSpan = element.querySelectorAll("label>span.pattern");
                const imageLabel = element.querySelectorAll("label");
                const imageSpanImage = element.querySelectorAll("label>span.expand>img");
                const inputList = element.querySelectorAll("input");

                inputList.forEach((item, index) => {
                    const image = selectedOptionOneVariants.filter(tmp => {
                        if (inputIndex == 0) return tmp.option1 == item.value;
                        if (inputIndex == 1) return tmp.option2 == item.value;
                        if (inputIndex == 2) return tmp.option3 == item.value;
                    });
    
                    if(image.length > 0) {
                        imageLabel[index].style.display = "inline-block";
                        if (imageSpan[index] != undefined && image[0].featured_image != null) imageSpan[index].style.backgroundImage = `url("${image[0].featured_image.src}")`;
                        if (imageSpanImage[index] != undefined && image[0].featured_image != null) imageSpanImage[index].srcset = image[0].featured_image.src;
                    }
                    // else {
                    //     imageLabel[index].style.display = "none";
                    // }
                })
            });
        }
    }            

    setInputAvailability(optionInputs, optionInputsValue, availableOptionInputsValue, knownValuesAtPosition, currentScopeHasLocalData) {
        // The currently selected other option(s) (e.g. colour) have NO variants at all in the
        // local data — this product's full variant list doesn't fit in what the page embeds, and
        // this specific selection fell outside it. We have no reliable local signal for any value
        // here, so leave whatever class was already rendered instead of asserting sold out.
        if (!currentScopeHasLocalData) return;

        optionInputs.forEach(input => {
            const value = input.getAttribute('value');
            // Absent from this.variantData entirely at this option slot (a real truncation gap) —
            // there's no local signal either way, so leave whatever class Liquid rendered instead
            // of asserting "sold out" for a value we have no data on. But a value that IS known at
            // this slot, just not for the currently selected other option(s), is a genuinely
            // nonexistent combination and must be marked sold out here rather than left showing a
            // stale "available" class carried over from a previous selection.
            if (!knownValuesAtPosition.includes(value)) return;

            if (availableOptionInputsValue.includes(value)) {
                input.classList.remove('soldout');
                input.innerText = value;
            } else {
                input.classList.add('soldout');
                optionInputsValue.includes(value) ? input.innerText = value + ' (Sold out)' : input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', value)
            }
        });
    }

    updateBadgeSale() {
        let badgeWrap = document.querySelector(".productView-badge");

        if (!badgeWrap) return;

        let badgeText = badgeWrap.querySelector(".productView-badge .badge.sale-badge"),
            badgeTextSaleType = badgeWrap.getAttribute('data-text-sale-badge'),
            priceCompare = document.querySelector(".productView-product .price__compare").getAttribute('data-compare'),
            priceLast = document.querySelector(".productView-product .price__sale .price__last").getAttribute('data-last'),
            percentSale = Math.round(((priceCompare - priceLast) / priceCompare) * 100);

        function renderSpan(text1, text2) {
            let spanElement = document.createElement('span');
            spanElement.classList.add('badge', 'sale-badge');
            let container = document.querySelector('.productView-badge');
            container.appendChild(spanElement);
            spanElement.innerText = `${text1} ${text2}%`
        }

        if (badgeWrap.classList.contains("has-badge-js")) {
            if (!priceCompare) {
                badgeWrap.querySelector(".badge.sale-badge")?.remove();
            } else {
                if (!badgeText) {
                    renderSpan(badgeTextSaleType, percentSale)
                } else {
                    badgeText.innerText = `${badgeTextSaleType} ${percentSale}%`
                }
            }
        } else {
            if (!priceCompare) {
                badgeWrap.querySelector(".badge.sale-badge")?.remove();
            } else {
                if (!badgeText) {
                    renderSpan(badgeTextSaleType, percentSale);
                } else {
                    badgeText.innerText = `${badgeTextSaleType}`
                }
            }
        }
    }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
    constructor() {
        super();
    }

    setInputAvailability(optionInputs, optionInputsValue, availableOptionInputsValue, knownValuesAtPosition, currentScopeHasLocalData) {
        // The currently selected other option(s) (e.g. colour) have NO variants at all in the
        // local data — this product's full variant list doesn't fit in what the page embeds, and
        // this specific selection fell outside it. We have no reliable local signal for any value
        // here, so leave whatever class was already rendered instead of asserting sold out.
        if (!currentScopeHasLocalData) return;

        optionInputs.forEach(input => {
            const value = input.getAttribute('value');
            // Absent from this.variantData entirely at this option slot (a real truncation gap) —
            // there's no local signal either way, so leave whatever class Liquid rendered instead
            // of asserting "sold out" for a value we have no data on. But a value that IS known at
            // this slot, just not for the currently selected other option(s), is a genuinely
            // nonexistent combination and must be marked sold out here rather than left showing a
            // stale "available" class carried over from a previous selection.
            if (!knownValuesAtPosition.includes(value)) return;

            if (availableOptionInputsValue.includes(value)) {
                input.nextSibling.classList.remove('soldout', 'unavailable');
                input.nextSibling.classList.add('available');
            } else {
                input.nextSibling.classList.remove('available', 'unavailable');
                input.nextSibling.classList.add('soldout');

                if (window.variantStrings.hide_variants_unavailable && !optionInputsValue.includes(value)) {
                    input.nextSibling.classList.add('unavailable')
                    if (!input.checked) return;
                    let inputsValue;
                    availableOptionInputsValue.length > 0 ? inputsValue = availableOptionInputsValue : inputsValue = optionInputsValue;
                    input.closest('.product-form__input').querySelector(`input[value="${inputsValue[0]}"]`).checked = true;
                    this.dispatchEvent(new Event('change'))
                }
            }
        });
    }
        
    updateOptions() {
        const fieldsets = Array.from(this.querySelectorAll('fieldset'));
        this.options = fieldsets.map((fieldset) => {
            return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
        });
    }
}

customElements.define('variant-radios', VariantRadios);

class QuantityInput extends HTMLElement {
    constructor() {
        super();
        if (this.closest('.quick-order-list__contents')) return; 
        this.input = this.querySelector('input');
        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', this.onInputChange.bind(this));

        this.querySelectorAll('button').forEach(
            (button) => button.addEventListener('click', this.onButtonClick.bind(this))
        );

        if (!this.checkHasMultipleVariants()) {
            this.initProductQuantity();
            this.checkVariantInventory();
        }
    }

    onInputChange(event) {
        event.preventDefault(); 
        var inputValue = this.input.value;
        var maxValue = parseInt(this.input.dataset.inventoryQuantity);
        var currentId = document.getElementById(`product-form-${this.input.dataset.product}`)?.querySelector('[name="id"]')?.value;
        var saleOutStock  = document.getElementById('product-add-to-cart')?.dataset.available === 'true' || false ;
        const addButton = document.getElementById(`product-form-${this.input.dataset.product}`)?.querySelector('[name="add"]');

        if(inputValue < 1) {
            inputValue = 1;

            this.input.value =  inputValue;
        }
        
        if (inputValue > maxValue && !saleOutStock && maxValue > 0) {
            var arrayInVarName = `selling_array_${this.input.dataset.product}`,
                itemInArray = window[arrayInVarName],
                itemStatus = itemInArray[currentId];
          
            if(itemStatus == 'deny') {
              inputValue = maxValue
              this.input.value = inputValue;
              const message = getInputMessage(maxValue)
              showWarning(message, 3000)
            }
        } else if (inputValue > maxValue && saleOutStock && maxValue <= 0) {
            this.input.value = inputValue;
        }

        if(window.subtotal.show) {
            var text,
                price = this.input.dataset.price,
                subTotal = 0;

            var parser = new DOMParser();

            subTotal = inputValue * price;
            subTotal = Shopify.formatMoney(subTotal, window.money_format);
            subTotal = extractContent(subTotal);

            const moneySpan = document.createElement('span')
            moneySpan.classList.add(window.currencyFormatted ? 'money' : 'money-subtotal') 
            moneySpan.innerText = subTotal 
            document.body.appendChild(moneySpan) 

            if (this.checkNeedToConvertCurrency()) {
                let currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');
                Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
            }

            subTotal = moneySpan.innerText 
            $(moneySpan).remove()

            if (window.subtotal.style == '1') {
                const pdView_subTotal = document.querySelector('.productView-subtotal .money') || document.querySelector('.productView-subtotal .money-subtotal');

                pdView_subTotal.textContent = subTotal;
            }
            else if (window.subtotal.style == '2') {
                text = window.subtotal.text.replace('[value]', subTotal);
                $('#product-sticky-add-to-cart').text(text);
                if (addButton != null){
                    addButton.textContent = text;
                }
            }

            const stickyPrice = $('[data-sticky-add-to-cart] .money-subtotal .money');
            const stickyComparePrice = $('[data-sticky-add-to-cart] .money-compare-price .money');

            if (stickyPrice.length) {
                stickyPrice.text(subTotal);
            }

            if (stickyComparePrice.length && window.subtotal.show) {
                let comparePrice = $('[data-sticky-add-to-cart] .money-compare-price').data('compare-price');
                comparePrice = inputValue * comparePrice;
                comparePrice = Shopify.formatMoney(comparePrice, window.money_format);
                comparePrice = extractContent(comparePrice);
                stickyComparePrice.text(comparePrice);
            }
        }

        if (this.classList.contains('quantity__group--2') || this.classList.contains('quantity__group--3')) {
            const mainQty = document.querySelector('.quantity__group--1 .quantity__input');
            if (mainQty != null){
                mainQty.value = inputValue;
            }

            const mainQty2Exists = !!document.querySelector('.quantity__group--2 .quantity__input');
            const mainQty3Exists = !!document.querySelector('.quantity__group--3 .quantity__input');

            if (this.classList.contains('quantity__group--2') && mainQty3Exists) {
                const mainQty3 = document.querySelector('.quantity__group--3 .quantity__input');
                mainQty3.value = inputValue;
            }
            else if (this.classList.contains('quantity__group--3') && mainQty2Exists) {
                const mainQty2 = document.querySelector('.quantity__group--2 .quantity__input');
                mainQty2.value = inputValue;
            }
        }
    }

    onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;
        
        event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
        if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    }

    checkNeedToConvertCurrency() {
        return (window.show_multiple_currencies && Currency.currentCurrency != shopCurrency) || window.show_auto_currency;
    }
    
    checkHasMultipleVariants() {
        const arrayInVarName = `product_inven_array_${this.querySelector('[data-product]').dataset.product}`
        this.inven_array = window[arrayInVarName];
        return Object.keys(this.inven_array).length > 1
    }
        
    initProductQuantity() {
        if(this.inven_array != undefined) {
            var inven_num = Object.values(this.inven_array),
                inventoryQuantity = parseInt(inven_num);

            this.querySelector('input[name="quantity"]').setAttribute('data-inventory-quantity', inventoryQuantity);
            this.querySelector('input[name="quantity"]').dataset.inventoryQuantity = inventoryQuantity
        }
    }

    checkVariantInventory() {
        const addBtn = document.getElementById('product-add-to-cart');
        if (!addBtn) return;
        this.input.disabled = addBtn.disabled;
        this.querySelector('.btn-quantity.minus').disabled = addBtn.disabled;
        this.querySelector('.btn-quantity.plus').disabled = addBtn.disabled;
    }

    getVariantData() {
        this.variantData = this.variantData || JSON.parse(document.querySelector('.product-option [type="application/json"]').textContent);
        return this.variantData;
    }
}

customElements.define('quantity-input', QuantityInput);

function hotStock(inventoryQuantity) {
    const productView = document.querySelector('.productView');
    const hotStock = productView.querySelector('.productView-hotStock');
    if(hotStock) {
        let hotStockText = hotStock.querySelector('.hotStock-text'),
            maxStock = parseFloat(hotStock.dataset.hotStock),
            textStock;

        if(inventoryQuantity > 0 && inventoryQuantity <= maxStock){
            hotStock.matches('.style-2') ? textStock  = window.inventory_text.hotStock2.replace('[inventory]', inventoryQuantity) : textStock  = window.inventory_text.hotStock.replace('[inventory]', inventoryQuantity);
            if (hotStockText) hotStockText.innerHTML = textStock;
            hotStock.classList.remove('is-hide');
        } else {
            hotStock.classList.add('is-hide');
        }

        if (hotStock.matches('.style-2')) {
            const invenProgress = inventoryQuantity / maxStock * 100,
                hotStockProgressItem = hotStock.querySelector('.hotStock-progress-item');
            if (hotStockProgressItem) hotStockProgressItem.style.width = `${invenProgress}%`;
        }
    }
}
const hotStockNoOptions = document.querySelector('.productView .productView-hotStock[data-current-inventory]');
if (hotStockNoOptions) {
    const inventoryQuantity = parseFloat(hotStockNoOptions.dataset.currentInventory);
    hotStock(inventoryQuantity);
}


function showWarning(content, time = null) {
    if (window.warningTimeout) {
        clearTimeout(window.warningTimeout);
    }
    const warningPopupContent = document.getElementById('halo-warning-popup').querySelector('[data-halo-warning-content]')
    warningPopupContent.textContent = content
    document.body.classList.add('has-warning')

    if (time) {
        window.warningTimeout = setTimeout(() => {
            document.body.classList.remove('has-warning')
        }, time)
    }
}

function getInputMessage(maxValue) {
    var message = window.cartStrings.addProductOutQuantity.replace('[maxQuantity]', maxValue);
    return message
}